import { AfterViewInit, Component, OnInit, ViewChild, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { HazardousSubstanceModel } from 'src/app/models/hazardous-substance.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { RBACService } from '../../shared/rbac.service';
import { UtilsService } from '../../shared/utils.service';

@Component({
    selector: 'app-hazardous-substances-view',
    templateUrl: './hazardous-substances-view.component.html',
    styleUrls: ['./hazardous-substances-view.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HazardousSubstancesViewComponent implements OnInit, AfterViewInit {
  hazardous_substance: HazardousSubstanceModel = new HazardousSubstanceModel();

  // RBAC permission flags. These are resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canEdit: boolean = false;
  canDelete: boolean = false;
  canViewSites: boolean = false;

  // columns to show and the data source
  linkedSitesDisplayedColumns: string[] = ['name', 'location', 'quantity', 'max_quantity_allowed', 'hs_location'];
  linkedSitesDataSource = new HazardousSubstancesLinkedSitesDataSource(
    this.app,
    this.api
  );

  // the paginator and sorter
  @ViewChild(MatPaginator) linkedSitesPaginator: MatPaginator;
  @ViewChild(MatSort) linkedSitesSort: MatSort;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    public renderer: Renderer2,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when we redirect away.
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDOUS_SUBSTANCES, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDOUS_SUBSTANCES, RBACService.RBAC_A.A_DELETE)
    ], true);
    this.canViewSites = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES, RBACService.RBAC_A.A_VIEW)
    ], true);

    // get the site id
    this.hazardous_substance.id = Number(
      this.route.snapshot.params['hazardous_substance_id']
    );

    // check if it's a valid id otherwise it's a new site being created.
    // NaN, NULL, 0, any sequence of letters will trigger a new site creation form.
    if (this.hazardous_substance.id) {
      this.linkedSitesDataSource.hazardous_substance_id = this.hazardous_substance.id;

      this.api.makeRequest(
        'get',
        'v1/hazardous_substances/' + this.hazardous_substance.id
      )
      .then((response) => {
        this.hazardous_substance.apply(response.data);
      })
      .catch((error) => {
        this.utils.showModal('Error', error.error?.message);
      });

      this.linkedSitesDataSource.getData();
    } else {
      this.router.navigate(['hazards']);
    }
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.linkedSitesSort.sortChange.subscribe(
      () => (this.linkedSitesPaginator.pageIndex = 0)
    );

    const _linkedSitesTap = tap(() => {
      this.linkedSitesDataSource.limit = this.linkedSitesPaginator.pageSize;
      this.linkedSitesDataSource.offset = this.linkedSitesPaginator.pageIndex;
      this.linkedSitesDataSource.sort_by = this.linkedSitesSort.active;
      this.linkedSitesDataSource.sort_order = this.linkedSitesSort.direction;

      this.linkedSitesDataSource.getData();
    });

    // subscribe to the paginator tap events
    this.linkedSitesPaginator.page.pipe(_linkedSitesTap).subscribe();
    this.linkedSitesSort.sortChange.pipe(_linkedSitesTap).subscribe();
  }

  onClearSearch() {
    this.linkedSitesDataSource.search = '';
    this.linkedSitesDataSource.getData(true);
  }

  onTabChanged(tabChangedEvent) {
    if (tabChangedEvent.tab.textLabel === 'Sites') {
      this.linkedSitesDataSource.getData(true);
    }
  }

  onRemove() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_HAZARDOUS_SUBSTANCES, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete hazardous substances.');
      return;
    }
    const singular = this.utils.getLangTerm('hazardous-substances.singular', 'Hazardous Substance');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.api.makeRequest(
          'delete',
          'v1/hazardous_substances/' + this.hazardous_substance.id
        )
        .then((response) => {
          this.utils.showToast(`The ${singular} was removed.`);
          this.router.navigate(['hazardous_substances']);
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  openDocument(url: string) {
    window.open(url, '_blank');
  }

  /**
   * Map the PPE to a simple array of titles and join them.
   * @param ppe
   */
  formattedPPE(ppe) {
    return ppe.map((ppe) => ppe.title).join(', ');
  }

  protected readonly RBACService = RBACService;
}

export class HazardousSubstancesLinkedSitesDataSource extends CustomDataSource {
  // record sorting and direction
  sort_by = 'name';
  sort_order = 'asc';

  hazardous_substance_id = 0;

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'hazardous_substances/' + this.hazardous_substance_id + '/linked-sites',
      resetOffset
    );
  }
}
